import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

export interface ContextualInfoPopoverProps {
  /** כותרת בתוך הפופ־אובר וב־aria */
  title: string;
  /** טקסט הסבר — תומך במעברי שורה */
  body: React.ReactNode;
  /** טולטיפ קצר בריחוף על האייקון (אם לא מועבר — משתמשים ב־title) */
  hoverHint?: string;
  /** ברירת מחדל: אייקון בכותרות ראשיות; dense: קטן יותר לטבלאות */
  size?: 'default' | 'dense';
}

/**
 * אייקון מידע (i) בצבע primary — ריחוף עם טולטיפ, לחיצה פותחת פופ־אובר עם כותרת ואייקון.
 * בכותרות מקטע (SubCard וכו'): הטקסט (Typography) לפני האייקון ב-DOM — ראה `.cursor/rules/ui-ux-entry-experience.mdc`.
 * ב־RTL: הכותרת ב-inline-start, האייקון ב-inline-end (אחרי הכותרת בסדר קריאה).
 */
export default function ContextualInfoPopover({
  title,
  body,
  hoverHint,
  size = 'default',
}: ContextualInfoPopoverProps) {
  const dense = size === 'dense';
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const titleId = React.useId();

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Tooltip
        title={hoverHint ?? title}
        arrow
        placement="top"
        enterDelay={250}
        slotProps={{
          tooltip: {
            sx: (theme) => ({
              maxWidth: 340,
              py: 1,
              px: 1.25,
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme.shadows[4],
              fontSize: '0.8125rem',
              lineHeight: 1.5,
              fontWeight: 400,
            }),
          },
          arrow: {
            sx: (theme) => ({
              color: theme.palette.background.paper,
            }),
          },
        }}
      >
        {/** עוטף קבוע — כמו בסרגל הניווט: בלי זה Tooltip גורם לכיווץ/אייקון דק בריחוף ב-flex */}
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            flexShrink: 0,
            lineHeight: 0,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            type="button"
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label={title}
            aria-expanded={open}
            aria-haspopup="dialog"
            sx={{
              flexShrink: 0,
              p: dense ? 0.35 : 0.5,
              '&:hover': { bgcolor: 'primary.lighter' },
            }}
          >
            <InfoOutlinedIcon
              sx={(theme) => ({
                fontSize: theme.typography.pxToRem(dense ? 18 : 20),
                flexShrink: 0,
              })}
            />
          </IconButton>
        </Box>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            elevation: 8,
            role: 'dialog',
            'aria-labelledby': titleId,
            sx: {
              p: dense ? 2 : 2.5,
              maxWidth: dense ? 400 : 440,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Stack spacing={dense ? 1.5 : 2}>
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={1.5}
            sx={{
              // ב־RTL: כותרת ראשונה (ימין), אייקון מידע אחריה — משמאל לכותרת
              '& .MuiSvgIcon-root': { flexShrink: 0 },
            }}
          >
            <Typography
              id={titleId}
              variant={dense ? 'subtitle1' : 'h6'}
              component="h2"
              sx={{ fontWeight: 600, lineHeight: 1.35 }}
            >
              {title}
            </Typography>
            <InfoOutlinedIcon
              sx={(theme) => ({
                color: 'text.secondary',
                mt: dense ? 0.1 : 0.25,
                fontSize: theme.typography.pxToRem(dense ? 18 : 22),
              })}
            />
          </Stack>
          <Box
            sx={{
              color: 'text.secondary',
              '& p': { m: 0, mb: 1.5, '&:last-child': { mb: 0 } },
            }}
          >
            {typeof body === 'string' ? (
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                {body}
              </Typography>
            ) : (
              body
            )}
          </Box>
        </Stack>
      </Popover>
    </>
  );
}
